import {
    type WritableComputedRef,
    type ComputedRef,
    type Ref,
    ref,
    watch,
    onMounted,
} from 'vue'
import { getWindow } from '@/helpers/globals'

// This function ensures that if the prop modelValue is changed simultaneously with either
// min or max, the updates will happen synchronously ans in the correct order.
// The correct order is:
// 1. change the input element min / max
// 2. change the input element value
// The change is done within the same stack, except when only modelValue is changed.
// If the change is not done in the correct order, the input element value stay within
// the old min / max
export function useInputRangeSynchronize(
    val: WritableComputedRef<number>,
    valueUi: WritableComputedRef<number>,
    minFinal: ComputedRef<number>,
    maxFinal: ComputedRef<number>
): {
    setValueFromInput: (event: Event) => void
    inputElement: Ref<HTMLInputElement | null, HTMLInputElement | null>
} {
    const setValueFromInput = (event: Event) => {
        const target = event.target as HTMLInputElement

        valueUi.value = Number(target.value)
    }
    const window = getWindow()
    const inputElement: Ref<HTMLInputElement | null> = ref(null)
    let queuedValue: number | null = null
    let queuedValueTimeout: number | null = null

    const clearTimeout = () => {
        if (window && queuedValueTimeout) {
            window.clearTimeout(queuedValueTimeout)

            queuedValueTimeout = null
        }
    }

    const setInputValue = () => {
        if (!inputElement.value) return

        queuedValue ||= 0

        inputElement.value.value = String(
            Math.min(Math.max(queuedValue, minFinal.value), maxFinal.value)
        )

        clearTimeout()
    }

    const handleValueUpdate = (newValue: number) => {
        queuedValue = newValue

        if (window) {
            clearTimeout()

            queuedValueTimeout = window.setTimeout(setInputValue)
        } else {
            val.value = newValue
        }
    }

    watch(val, handleValueUpdate)

    const handleMinMaxUpdate = ([min, max]: [number, number]) => {
        if (!inputElement.value) return

        inputElement.value.min = String(min)
        inputElement.value.max = String(max)

        if (queuedValue !== null) {
            setInputValue()
        }
    }

    watch([minFinal, maxFinal], handleMinMaxUpdate)

    onMounted(() => {
        handleMinMaxUpdate([minFinal.value, maxFinal.value])
        handleValueUpdate(val.value)
    })

    return {
        setValueFromInput,
        inputElement,
    }
}
